import React, { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Target, TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Forecast Accuracy Component
 * Shows projected vs actual commission comparison for a month
 * MA-IMPL-118B: Added campaignId filter support
 * 
 * Used in Operator → Monthly Review section
 */
const ForecastAccuracy = ({ month = null, vertical = "all", campaignId, className = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (month) {
          params.append("month", month);
        }
        if (vertical && vertical !== "all") {
          params.append("vertical", vertical);
        }
        // MA-IMPL-118B: Include campaign filter
        if (campaignId && campaignId !== "all") {
          params.append("campaign_id", campaignId);
        }
        const res = await api.get(`/analytics/forecast-vs-actual?${params.toString()}`);
        setData(res.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load forecast data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [month, vertical, campaignId]);

  if (loading) {
    return (
      <div className={`bg-white/5 rounded-lg p-4 border border-white/10 ${className}`} data-testid="forecast-accuracy">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white/5 rounded-lg p-4 border border-red-500/20 ${className}`} data-testid="forecast-accuracy">
        <div className="text-xs text-red-400">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { 
    projected_commission_start_of_month: projected,
    actual_issued_commission: actual,
    variance_percent: variance,
    has_snapshot,
    month: monthStr
  } = data;

  // Format currency
  const formatCurrency = (val) => {
    if (!val && val !== 0) return '—';
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
    return `$${val.toFixed(0)}`;
  };

  // Format variance
  const formatVariance = (v) => {
    if (!v && v !== 0) return '—';
    const pct = (v * 100).toFixed(1);
    return v >= 0 ? `+${pct}%` : `${pct}%`;
  };

  // Determine variance color
  const getVarianceColor = (v) => {
    if (!v && v !== 0) return 'text-slate-400';
    if (v > 0.05) return 'text-emerald-400';
    if (v < -0.15) return 'text-red-400';
    if (v < -0.05) return 'text-amber-400';
    return 'text-slate-300';
  };

  // Variance icon
  const VarianceIcon = () => {
    if (!variance && variance !== 0) return <Minus className="h-3.5 w-3.5 text-slate-400" />;
    if (variance > 0.01) return <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />;
    if (variance < -0.01) return <TrendingDown className="h-3.5 w-3.5 text-red-400" />;
    return <Minus className="h-3.5 w-3.5 text-slate-400" />;
  };

  return (
    <div 
      className={`bg-white/5 rounded-lg p-4 border border-white/10 ${className}`} 
      data-testid="forecast-accuracy"
    >
      <div className="flex items-center gap-2 mb-3">
        <Target className="h-4 w-4 text-slate-400" />
        <span className="text-sm font-medium text-slate-200">Forecast Accuracy</span>
        <span className="text-xs text-slate-500">({monthStr})</span>
      </div>

      {!has_snapshot && !projected ? (
        <div className="text-xs text-slate-500">
          No forecast snapshot available for this month.
          Snapshots are created automatically at the start of each month.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {/* Projected */}
          <div className="text-center p-2 bg-white/5 rounded" data-testid="forecast-projected">
            <div className="text-xs text-slate-500 mb-1">Projected</div>
            <div className="text-lg font-semibold text-slate-200">
              {formatCurrency(projected)}
            </div>
          </div>

          {/* Actual */}
          <div className="text-center p-2 bg-white/5 rounded" data-testid="forecast-actual">
            <div className="text-xs text-slate-500 mb-1">Actual</div>
            <div className="text-lg font-semibold text-slate-200">
              {formatCurrency(actual)}
            </div>
          </div>

          {/* Variance */}
          <div className="text-center p-2 bg-white/5 rounded" data-testid="forecast-variance">
            <div className="text-xs text-slate-500 mb-1">Variance</div>
            <div className={`text-lg font-semibold flex items-center justify-center gap-1 ${getVarianceColor(variance)}`}>
              <VarianceIcon />
              {formatVariance(variance)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastAccuracy;
